import { TIME_CONSTANTS, TIME_MARKS_CONFIG, TIME_RANGE_LABEL } from './constants';
import type { TimeMark, TimeRange } from './types';

/**
 * Converts time string in HH:MM format to total minutes
 */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * TIME_CONSTANTS.MINUTES_PER_HOUR + minutes;
};

/**
 * Converts total minutes to time string in HH:MM format
 */
export const minutesToTime = (minutes: number, showMinutes = true): string => {
  const hours = Math.floor(minutes / TIME_CONSTANTS.MINUTES_PER_HOUR);
  const mins = minutes % TIME_CONSTANTS.MINUTES_PER_HOUR;
  return `${hours.toString().padStart(TIME_CONSTANTS.PADDING_LENGTH, TIME_CONSTANTS.PADDING_CHAR)}${showMinutes ? `:${mins.toString().padStart(TIME_CONSTANTS.PADDING_LENGTH, TIME_CONSTANTS.PADDING_CHAR)}` : 'h'}`;
};

/**
 * Calculates time range data from time strings
 */
export const calculateTimeRange = (startTime: string, endTime: string, minTime: string, maxTime: string): TimeRange => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const minMinutes = timeToMinutes(minTime);
  const maxMinutes = timeToMinutes(maxTime);
  const totalRange = maxMinutes - minMinutes;

  return {
    startMinutes,
    endMinutes,
    minMinutes,
    maxMinutes,
    totalRange,
  };
};

/**
 * Generates time marks for the slider based on the time range
 */
export const generateTimeMarks = (timeRange: TimeRange): TimeMark[] => {
  const { minMinutes, maxMinutes } = timeRange;
  const marks: TimeMark[] = [];
  const maxMarks = TIME_MARKS_CONFIG.MAX_MARKS;
  const range = maxMinutes - minMinutes;
  const interval = range / (maxMarks - 1);

  for (let i = 0; i < maxMarks; i++) {
    const minutes = Math.round(minMinutes + i * interval);
    const position = Math.round(((minutes - minMinutes) / range) * 100);
    const time = minutesToTime(minutes);
    marks.push({ position, time });
  }

  // add last mark
  // add last mark only if not already present
  const lastMark = marks[marks.length - 1];
  const lastMarkTime = minutesToTime(maxMinutes);
  if (lastMark.position !== 100 || lastMark.time !== lastMarkTime) {
    marks.push({ position: 100, time: lastMarkTime });
  }

  return marks;
};

/**
 * Formats time range duration for display
 */
export const formatTimeRange = (startMinutes: number, endMinutes: number): string => {
  const diff = endMinutes - startMinutes;
  const hours = Math.floor(diff / TIME_CONSTANTS.MINUTES_PER_HOUR);
  const minutes = diff % TIME_CONSTANTS.MINUTES_PER_HOUR;
  const formattedHours = hours.toString().padStart(TIME_CONSTANTS.PADDING_LENGTH, TIME_CONSTANTS.PADDING_CHAR);
  const formattedMinutes = minutes.toString().padStart(TIME_CONSTANTS.PADDING_LENGTH, TIME_CONSTANTS.PADDING_CHAR);

  return `${TIME_RANGE_LABEL.PREFIX}${formattedHours}:${formattedMinutes}${TIME_RANGE_LABEL.SUFFIX}`;
};
